// Film lying on the floor, wired at its near end: one cable crimped onto each
// copper busbar. Current runs down both cables, the busbars light up and the
// heat spreads from both edges toward the centre of the film.
(() => {
  const canvas = document.querySelector('canvas[data-floor]');
  if (!canvas) return;
  const figure = canvas.closest('figure');
  const fallback = figure.querySelector('[data-floor-fallback]');
  const controls = figure.querySelector('[data-floor-controls]');
  const toggle = figure.querySelector('[data-floor-toggle]');
  const replay = figure.querySelector('[data-floor-replay]');
  function init() {
  // Bound the compositing surfaces; the source photographs stay full resolution.
  canvas.width = Math.min(1440, Math.max(720, Math.round(figure.clientWidth * Math.min(devicePixelRatio || 1, 2))));
  canvas.height = Math.round(canvas.width * 2 / 3);
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const W = canvas.width, H = canvas.height;
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const geo = JSON.parse(canvas.dataset.floorGeo);
  const P = ([x, y]) => ({ x: x * W, y: y * H });
  const lerp = (a, b, t) => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
  const dist = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);

  const edge = geo.edge.map(P), far = geo.far.map(P);
  const bus = geo.bus.map(([a, b]) => [P(a), P(b)]);
  const plugs = geo.plugs.map(P);
  const k = dist(edge[0], edge[1]) / 800;            // px per mm at the near end
  const kFar = dist(far[0], far[1]) / 800;           // px per mm at the far end
  const cableWidth = 4.2 * k;

  // Each cable leaves its crimp outward, then drops out of the frame.
  const cables = plugs.map((p, i) => {
    const side = i === 0 ? -1 : 1;
    const start = { x: p.x + side * 14 * k, y: p.y };
    return [
      start,
      { x: start.x + side * 40 * k, y: start.y + 4 * k },
      { x: start.x + side * 30 * k, y: H * 0.95 },
      { x: start.x + side * 10 * k, y: H + 60 },
    ];
  });
  const bezier = (c, t) => {
    const [a, b, d, e] = c, u = 1 - t;
    return {
      x: u * u * u * a.x + 3 * u * u * t * b.x + 3 * u * t * t * d.x + t * t * t * e.x,
      y: u * u * u * a.y + 3 * u * u * t * b.y + 3 * u * t * t * d.y + t * t * t * e.y,
    };
  };

  const load = (src) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

  Promise.all([load(canvas.dataset.floorBase), load(canvas.dataset.floorGlow)]).then(([base, glow]) => {
    const bloom = document.createElement('canvas');
    bloom.width = W; bloom.height = H;
    const bg = bloom.getContext('2d');
    bg.filter = 'blur(12px)';
    bg.drawImage(glow, 0, 0, W, H);
    const heat = document.createElement('canvas');
    heat.width = W; heat.height = H;
    const hg = heat.getContext('2d');
    const mask = document.createElement('canvas');
    mask.width = W; mask.height = H;
    const mg = mask.getContext('2d');

    const cablePath = (c) => {
      ctx.beginPath();
      ctx.moveTo(c[0].x, c[0].y);
      ctx.bezierCurveTo(c[1].x, c[1].y, c[2].x, c[2].y, c[3].x, c[3].y);
    };

    function drawCable(c, power) {
      ctx.save();
      ctx.lineCap = 'round';
      cablePath(c);
      ctx.shadowColor = 'rgba(0,0,0,.5)';
      ctx.shadowBlur = 22;
      ctx.shadowOffsetY = 12;
      ctx.strokeStyle = '#5e0b10';
      ctx.lineWidth = cableWidth + 6;
      ctx.stroke();
      ctx.shadowColor = 'transparent';
      ctx.strokeStyle = '#b3121c';
      ctx.lineWidth = cableWidth;
      ctx.stroke();
      ctx.strokeStyle = 'rgba(255,190,180,.5)';
      ctx.lineWidth = cableWidth * 0.18;
      ctx.translate(-cableWidth * 0.15, -cableWidth * 0.2);
      ctx.stroke();
      ctx.restore();
      if (power > 0) {
        ctx.save();
        ctx.globalCompositeOperation = 'lighter';
        cablePath(c);
        ctx.strokeStyle = `rgba(255,70,40,${0.3 * power})`;
        ctx.lineWidth = cableWidth * 2.2;
        ctx.filter = 'blur(14px)';
        ctx.stroke();
        ctx.restore();
      }
    }

    function drawConnector(p, i, power) {
      // Crimp clip across the busbar, barrel toward the outer edge.
      const [n, f] = bus[i];
      const angle = Math.atan2(f.y - n.y, f.x - n.x) + Math.PI / 2;
      const side = i === 0 ? -1 : 1;
      const w = 19 * k, h = 13 * k;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(angle);
      ctx.shadowColor = 'rgba(0,0,0,.4)';
      ctx.shadowBlur = 16;
      ctx.shadowOffsetY = 8;
      const barrel = ctx.createLinearGradient(0, -h * 0.28, 0, h * 0.28);
      barrel.addColorStop(0, '#f2f2ef'); barrel.addColorStop(0.45, '#9d9f9f'); barrel.addColorStop(1, '#5b5d5e');
      ctx.fillStyle = barrel;
      ctx.beginPath();
      const bx = side < 0 ? -w / 2 - 12 * k : w / 2 - 4 * k;
      ctx.roundRect(bx, -h * 0.28, 16 * k, h * 0.56, h * 0.2);
      ctx.fill();
      const clip = ctx.createLinearGradient(-w / 2, -h / 2, w / 2, h / 2);
      clip.addColorStop(0, '#fbfbf8'); clip.addColorStop(0.35, '#b9bbbb'); clip.addColorStop(0.7, '#e3e4e1'); clip.addColorStop(1, '#6d6f70');
      ctx.fillStyle = clip;
      ctx.beginPath();
      ctx.roundRect(-w / 2, -h / 2, w, h, h * 0.18);
      ctx.fill();
      ctx.shadowColor = 'transparent';
      ctx.strokeStyle = 'rgba(40,40,40,.45)';
      ctx.lineWidth = 2;
      for (const s of [-0.25, 0, 0.25]) {
        ctx.beginPath();
        ctx.moveTo(s * w, -h * 0.38);
        ctx.lineTo(s * w, h * 0.38);
        ctx.stroke();
      }
      if (power > 0) {
        ctx.globalCompositeOperation = 'lighter';
        const g = ctx.createRadialGradient(0, 0, 0, 0, 0, w * 1.4);
        g.addColorStop(0, `rgba(255,190,120,${0.5 * power})`);
        g.addColorStop(1, 'rgba(255,90,40,0)');
        ctx.fillStyle = g;
        ctx.fillRect(-w * 1.5, -w * 1.5, w * 3, w * 3);
      }
      ctx.restore();
    }

    function drawSparks(c, t, offset) {
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      const count = 6;
      for (let i = 0; i < count; i += 1) {
        const p = 1 - (((t * 0.16) + offset + i / count) % 1);   // toward the film
        const pt = bezier(c, p);
        const fade = Math.min(1, p * 6, (1 - p) * 8);
        const r = cableWidth * 1.6;
        const g = ctx.createRadialGradient(pt.x, pt.y, 0, pt.x, pt.y, r);
        g.addColorStop(0, `rgba(255,245,220,${0.95 * fade})`);
        g.addColorStop(0.35, `rgba(255,140,70,${0.55 * fade})`);
        g.addColorStop(1, 'rgba(255,60,30,0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }

    function drawBusbars(t, level) {
      if (level <= 0) return;
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      for (const [n, f] of bus) {
        // Soft glow along the busbar, narrowing with perspective.
        const wN = 12.5 * k, wF = 12.5 * kFar;
        const reach = Math.min(1, level);
        const end = lerp(n, f, reach);
        const nx = -(f.y - n.y) / dist(n, f), ny = (f.x - n.x) / dist(n, f);
        const wE = wN + (wF - wN) * reach;
        const g = ctx.createLinearGradient(n.x, n.y, end.x, end.y);
        g.addColorStop(0, `rgba(255,130,55,${0.3 * reach})`);
        g.addColorStop(1, 'rgba(255,130,55,0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.moveTo(n.x + nx * wN, n.y + ny * wN);
        ctx.lineTo(end.x + nx * wE, end.y + ny * wE);
        ctx.lineTo(end.x - nx * wE, end.y - ny * wE);
        ctx.lineTo(n.x - nx * wN, n.y - ny * wN);
        ctx.closePath();
        ctx.fill();
        for (let i = 0; i < 3; i += 1) {
          const s = ((t * 0.12 + i / 3) % 1) * reach;
          const pt = lerp(n, f, s);
          const r = (12.5 * (k + (kFar - k) * s)) * 1.8;
          const pg = ctx.createRadialGradient(pt.x, pt.y, 0, pt.x, pt.y, r);
          pg.addColorStop(0, `rgba(255,190,130,${0.4 * reach})`);
          pg.addColorStop(1, 'rgba(255,120,50,0)');
          ctx.fillStyle = pg;
          ctx.fillRect(pt.x - r, pt.y - r, r * 2, r * 2);
        }
      }
      ctx.restore();
    }

    function drawHeat(spread, breathe) {
      if (spread <= 0) return;
      // Reveal mask: two bands growing from the busbars toward the centre.
      mg.clearRect(0, 0, W, H);
      mg.filter = 'blur(40px)';
      mg.fillStyle = '#000';
      const half = Math.min(0.5, spread * 0.56);
      const [l, r] = bus;
      for (const [a, b] of [[l, r], [r, l]]) {
        const inN = lerp(a[0], b[0], half), inF = lerp(a[1], b[1], half);
        const outN = lerp(a[0], b[0], -0.06), outF = lerp(a[1], b[1], -0.06);
        mg.beginPath();
        mg.moveTo(outN.x, outN.y + 40); mg.lineTo(inN.x, inN.y + 40);
        mg.lineTo(inF.x, inF.y - 40); mg.lineTo(outF.x, outF.y - 40);
        mg.closePath();
        mg.fill();
      }
      mg.filter = 'none';
      hg.clearRect(0, 0, W, H);
      hg.globalCompositeOperation = 'source-over';
      hg.drawImage(glow, 0, 0, W, H);
      hg.globalCompositeOperation = 'lighter';
      hg.drawImage(glow, 0, 0, W, H);
      hg.drawImage(bloom, 0, 0);
      hg.globalCompositeOperation = 'destination-in';
      hg.drawImage(mask, 0, 0);
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      ctx.globalAlpha = breathe;
      ctx.drawImage(heat, 0, 0);
      ctx.globalAlpha = 1;
      const c = lerp(lerp(edge[0], edge[1], 0.5), lerp(far[0], far[1], 0.5), 0.35);
      const warm = ctx.createRadialGradient(c.x, c.y, 0, c.x, c.y, W * 0.5);
      warm.addColorStop(0, `rgba(255,70,30,${0.14 * spread * breathe})`);
      warm.addColorStop(1, 'rgba(255,40,20,0)');
      ctx.fillStyle = warm;
      ctx.fillRect(0, 0, W, H);
      ctx.restore();
    }

    // Rising warmth from the film toward the air label.
    function drawWaves(t, level) {
      if (level <= 0) return;
      const top = H * 0.2, bottom = H * 0.46;
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      ctx.lineCap = 'round';
      for (let i = 0; i < 5; i += 1) {
        const x0 = W * (0.38 + i * 0.06);
        const grad = ctx.createLinearGradient(0, bottom, 0, top);
        grad.addColorStop(0, 'rgba(255,120,80,0)');
        grad.addColorStop(0.35, `rgba(255,140,100,${0.2 * level})`);
        grad.addColorStop(1, 'rgba(255,160,120,0)');
        ctx.strokeStyle = grad;
        ctx.lineWidth = 3;
        ctx.setLineDash([26, 22]);
        ctx.lineDashOffset = t * 26 + i * 17;
        ctx.beginPath();
        for (let y = bottom; y >= top; y -= 8) {
          const x = x0 + Math.sin(y * 0.018 + t * 0.9 + i) * 10;
          if (y === bottom) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
      ctx.restore();
    }

    // Temperature readouts: the film reaches 28 °C, the room air settles at 23 °C.
    const filmOut = figure && figure.querySelector('[data-temp-film]');
    const airOut = figure && figure.querySelector('[data-temp-air]');
    let shown = [null, null];
    function temps(t, cooling) {
      const ease = (a, b) => { const x = Math.min(1, Math.max(0, (t - a) / (b - a))); return x * x * (3 - 2 * x); };
      const film = Math.round(20 + 8 * ease(4.5, 11.5) * cooling);
      const air = Math.round(20 + 3 * ease(7, 16) * cooling);
      if (filmOut && film !== shown[0]) filmOut.textContent = film;
      if (airOut && air !== shown[1]) airOut.textContent = air;
      shown = [film, air];
    }

    function frame(time, still = false) {
      // 24-second cycle: warm up, hold, then gently fade back for the next ignition.
      const t = still ? 18 : (time / 1000) % 24;
      const ease = (a, b) => Math.min(1, Math.max(0, (t - a) / (b - a)));
      const fade = ease(20, 24);
      const cooling = 1 - fade * fade * (3 - 2 * fade);
      const current = ease(0, 1.5) * cooling;
      const busLevel = ease(3, 5.5) * cooling;
      const spread = ease(4.5, 11) * cooling;
      const breathe = 0.9 + 0.1 * Math.sin(t * 0.7);

      ctx.clearRect(0, 0, W, H);
      ctx.drawImage(base, 0, 0, W, H);
      ctx.fillStyle = `rgba(8,4,4,${0.25 * (1 - spread)})`;
      ctx.fillRect(0, 0, W, H);
      drawHeat(spread, breathe);
      drawBusbars(t, busLevel);
      cables.forEach((c) => drawCable(c, current));
      plugs.forEach((p, i) => drawConnector(p, i, Math.max(current * 0.5, busLevel)));
      if (current > 0) {
        ctx.save();
        ctx.globalAlpha = current;
        cables.forEach((c, i) => drawSparks(c, t, i * 0.37));
        ctx.restore();
      }
      drawWaves(t, spread);
      temps(t, cooling);
    }

    // Elapsed time advances only while visible and playing, without jumps on resume.
    let elapsed = 0, lastTime = null, lastPaint = 0, raf = 0, running = false;
    let visible = false, localPaused = false;
    let globalPaused = document.documentElement.dataset.motionPaused === 'true';
    const stopped = () => localPaused || globalPaused || reducedMotion.matches;
    function updateControls() {
      const paused = stopped();
      toggle.textContent = paused ? 'Reanudar' : 'Pausar';
      toggle.setAttribute('aria-label', paused ? 'Reanudar animación del film' : 'Pausar animación del film');
      toggle.setAttribute('aria-pressed', String(paused));
      toggle.disabled = reducedMotion.matches;
      replay.disabled = reducedMotion.matches;
    }
    const loop = (now) => {
      if (!running) return;
      if (lastTime !== null) elapsed += now - lastTime;
      lastTime = now;
      if (now - lastPaint >= 1000 / 24) {
        frame(elapsed);
        lastPaint = now;
      }
      raf = requestAnimationFrame(loop);
    };
    const stop = () => {
      running = false;
      cancelAnimationFrame(raf);
      lastTime = null;
      canvas.dataset.floorPlaying = 'false';
    };
    function sync() {
      updateControls();
      if (!visible || document.hidden || stopped()) {
        stop();
        if (reducedMotion.matches) frame(0, true);
      } else if (!running) {
        running = true;
        canvas.dataset.floorPlaying = 'true';
        raf = requestAnimationFrame(loop);
      }
    }
    toggle.addEventListener('click', () => {
      if (stopped()) {
        localPaused = false;
        window.dispatchEvent(new CustomEvent('underheat:motion-request', { detail: { paused: false } }));
      } else localPaused = true;
      sync();
    });
    replay.addEventListener('click', () => {
      stop();
      elapsed = 0;
      lastPaint = 0;
      localPaused = false;
      frame(0);
      window.dispatchEvent(new CustomEvent('underheat:motion-request', { detail: { paused: false } }));
      sync();
    });
    window.addEventListener('underheat:motion', event => { globalPaused = event.detail.paused; sync(); });
    document.addEventListener('visibilitychange', sync);
    reducedMotion.addEventListener('change', sync);
    frame(0, reducedMotion.matches || globalPaused);
    canvas.hidden = false;
    fallback.hidden = true;
    controls.hidden = false;
    canvas.dataset.floorReady = '';
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(entries => {
        visible = entries[0].isIntersecting;
        sync();
      }, { threshold: 0.15 }).observe(canvas);
    } else { visible = true; sync(); }
    updateControls();
  }).catch(() => {
    canvas.hidden = true;
    fallback.hidden = false;
    controls.hidden = true;
  });
  }
  // Keep the still visible without JS or if images cannot load; fetch near the section.
  if ('IntersectionObserver' in window) {
    const loader = new IntersectionObserver(entries => {
      if (!entries[0].isIntersecting) return;
      loader.disconnect();
      init();
    }, { rootMargin: '400px' });
    loader.observe(figure);
  } else init();
})();
